import {
  ITEM_KINDS,
  RECOVERY_CREDIT_POLICIES,
  RELEASE_POLICIES,
  type CreateItemRequest,
  type ItemDto,
  type ItemKind,
  type RecoveryCreditPolicy,
  type ReleasePolicy,
  type UpdateItemRequest,
} from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  PageHeader,
  Spinner,
  Tooltip,
  inputClass,
} from '../components/ui';
import { CodeField } from '../components/CodeField';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { ApiError, api } from '../lib/api';
import { humanise } from '../lib/format';
import { Can, useSession } from '../lib/session';

const KIND_TONES: Record<ItemKind, 'green' | 'accent' | 'muted' | 'dark'> = {
  raw_material: 'green',
  packaging: 'accent',
  intermediate: 'muted',
  finished_good: 'dark',
  other: 'muted',
};

interface FormState {
  code: string;
  name: string;
  kind: ItemKind;
  baseUom: string;
  trackLots: boolean;
  shelfLifeDays: string;
  reorderPoint: string;
  recoveredHandling: ReleasePolicy;
  recoveryCreditPolicy: RecoveryCreditPolicy;
  recoveryCreditPercent: string;
}

const EMPTY_FORM: FormState = {
  code: '',
  name: '',
  kind: 'raw_material',
  baseUom: 'kg',
  trackLots: true,
  shelfLifeDays: '',
  reorderPoint: '',
  recoveredHandling: 'quarantine',
  recoveryCreditPolicy: 'percent_of_input_cost',
  recoveryCreditPercent: '100',
};

function formFromItem(item: ItemDto): FormState {
  return {
    code: item.code,
    name: item.name,
    kind: item.kind,
    baseUom: item.baseUom,
    trackLots: item.trackLots,
    shelfLifeDays: item.shelfLifeDays === null ? '' : String(item.shelfLifeDays),
    reorderPoint: item.reorderPoint ?? '',
    recoveredHandling: item.recoveredHandling,
    recoveryCreditPolicy: item.recoveryCreditPolicy,
    recoveryCreditPercent: item.recoveryCreditPercent,
  };
}

function errorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.details && Array.isArray(error.details)) {
      const first = error.details[0] as { path?: string; message?: string };
      return `${error.message}: ${first.path} ${first.message}`;
    }
    return error.message;
  }
  return fallback;
}

export function ItemsPage() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canManage = can('masterdata.manage');
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const listQuery = useQuery({
    queryKey: ['items', { search, includeInactive }],
    queryFn: () => api.items.list({ search, includeInactive, limit: 100 }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['items'] });
  }

  const saveMutation = useMutation({
    mutationFn: (variables: { id?: string; payload: CreateItemRequest | UpdateItemRequest }) =>
      variables.id
        ? api.items.update(variables.id, variables.payload as UpdateItemRequest)
        : api.items.create(variables.payload as CreateItemRequest),
    onSuccess: () => {
      invalidate();
      closeForm();
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (item: ItemDto) => api.items.update(item.id, { isActive: !item.isActive }),
    onSuccess: invalidate,
  });

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    saveMutation.reset();
    setFormOpen(true);
  }

  function openEdit(item: ItemDto) {
    setEditing(item);
    setForm(formFromItem(item));
    saveMutation.reset();
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const payload: CreateItemRequest = {
      code: form.code.trim(),
      name: form.name.trim(),
      kind: form.kind,
      baseUom: form.baseUom.trim(),
      trackLots: form.trackLots,
      recoveredHandling: form.recoveredHandling,
      recoveryCreditPolicy: form.recoveryCreditPolicy,
      recoveryCreditPercent: form.recoveryCreditPercent.trim() || '100',
      ...(form.shelfLifeDays.trim() ? { shelfLifeDays: Number(form.shelfLifeDays) } : {}),
      ...(form.reorderPoint.trim() ? { reorderPoint: form.reorderPoint.trim() } : {}),
    };

    if (editing) {
      // `code` is immutable once created: it is the human key printed on labels.
      const { code: _code, ...rest } = payload;
      saveMutation.mutate({ id: editing.id, payload: rest });
    } else {
      saveMutation.mutate({ payload });
    }
  }

  const items = listQuery.data?.items ?? [];

  const itemColumns: DataColumn<ItemDto>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (item) => <span className="font-mono text-xs">{item.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      primary: true,
      cell: (item) => <span className="font-medium text-ink">{item.name}</span>,
    },
    {
      key: 'kind',
      header: 'Kind',
      cell: (item) => <Badge tone={KIND_TONES[item.kind]}>{humanise(item.kind)}</Badge>,
    },
    { key: 'uom', header: 'Base UoM', cell: (item) => item.baseUom },
    { key: 'reorder', header: 'Reorder at', align: 'right', cell: (item) => item.reorderPoint ?? '—' },
    {
      key: 'recovered',
      header: 'Recovered handling',
      cell: (item) => (
        <Badge tone={item.recoveredHandling === 'quarantine' ? 'hazard' : 'green'}>
          {humanise(item.recoveredHandling)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (item) =>
        item.isActive ? <Badge tone="green">Active</Badge> : <Badge tone="muted">Inactive</Badge>,
    },
  ];

  const itemActions = canManage
    ? (item: ItemDto) => (
        <>
          <Button tone="ghost" onClick={() => openEdit(item)}>
            Edit
          </Button>
          <Button
            tone={item.isActive ? 'danger' : 'ghost'}
            onClick={() => toggleMutation.mutate(item)}
          >
            {item.isActive ? 'Deactivate' : 'Reactivate'}
          </Button>
        </>
      )
    : undefined;

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Items"
        description="Materials and products. Every quantity in the system belongs to exactly one item, and every item stores its quantities in one base unit of measure."
        actions={
          <Can capability="masterdata.manage">
            <Button onClick={openCreate}>New item</Button>
          </Can>
        }
      />

      {formOpen ? (
        <Card className="mb-6">
          <CardBody>
            <h2 className="text-base font-bold text-ink">
              {editing ? `Edit ${editing.code}` : 'New item'}
            </h2>
            <form
              autoComplete="off"
              onSubmit={handleSubmit}
              className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              <CodeField
                label="Code"
                value={form.code}
                onChange={(code) => setForm((current) => ({ ...current, code }))}
                queryKey={['item-suggest', form.kind, form.name.trim()]}
                queryFn={
                  !editing && form.name.trim().length >= 2
                    ? () => api.items.suggestCode({ kind: form.kind, name: form.name.trim() })
                    : undefined
                }
                disabled={Boolean(editing)}
                hint={editing ? 'Codes are immutable' : undefined}
                tooltip="Built from the item's kind and a short mnemonic, then a sequence number — RM-SHP-001 is the first sodium hydroxide pellets item. Accept the suggestion or type your own code; either way it is fixed once the item is created, because labels and audit rows point at it."
                error={
                  !editing && saveMutation.error instanceof ApiError && saveMutation.error.status === 409
                    ? saveMutation.error.message
                    : undefined
                }
              />

              <Field label="Name">
                <input
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="5-Chlorothiophene-2-carboxylic acid"
                />
              </Field>

              <Field
                label="Kind"
                tooltip="What the item is used for. Only finished goods and intermediates can be produced from a recipe; the other kinds are consumed or sold as they are."
              >
                <select
                  className={inputClass}
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value as ItemKind })}
                >
                  {ITEM_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {humanise(kind)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Base unit of measure"
                hint="All stock is stored in this unit"
                tooltip="The unit every quantity of this item is stored and summed in. Staff can enter other units the item defines (grams, litres) and the system converts them to this one, so on-hand is always a single comparable number."
              >
                <input
                  className={inputClass}
                  value={form.baseUom}
                  onChange={(e) => setForm({ ...form, baseUom: e.target.value })}
                  required
                  placeholder="kg"
                />
              </Field>

              <Field
                label="Reorder point"
                hint="Low-stock threshold, blank = not tracked"
                tooltip="When the total on hand across all locations falls to this figure or below, the item appears in the low-stock list on the Inventory page. It is only a prompt to buy more — it never blocks anything."
              >
                <input
                  className={inputClass}
                  value={form.reorderPoint}
                  onChange={(e) => setForm({ ...form, reorderPoint: e.target.value })}
                  placeholder="20"
                  inputMode="decimal"
                />
              </Field>

              <Field
                label="Shelf life (days)"
                hint="Blank = does not expire"
                tooltip="How long the item stays good after it is received. New lots get an expiry date calculated from this, unless the delivery note gives one. Expiry is only reported, never enforced."
              >
                <input
                  className={inputClass}
                  value={form.shelfLifeDays}
                  onChange={(e) => setForm({ ...form, shelfLifeDays: e.target.value })}
                  placeholder="730"
                  inputMode="numeric"
                />
              </Field>

              <Field
                label="Recovered material from this item"
                hint="Quarantine holds recovered lots until QC releases them"
                tooltip="The default for material recovered from this item. Quarantine creates the recovered lot but holds it out of production until a quality check releases it; immediate makes it usable straight away. It can be overridden for a single recovery, with a reason."
              >
                <select
                  className={inputClass}
                  value={form.recoveredHandling}
                  onChange={(e) =>
                    setForm({ ...form, recoveredHandling: e.target.value as ReleasePolicy })
                  }
                >
                  {RELEASE_POLICIES.map((policy) => (
                    <option key={policy} value={policy}>
                      {humanise(policy)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Recovery valuation"
                hint="How a recovered lot is costed"
                tooltip="Sets what a recovered lot is worth. “Percent of input cost” values it as a share of what the contributing lots cost; “latest cost” copies the newest contributing lot's cost; “manual” means the person recording the recovery must type a cost."
              >
                <select
                  className={inputClass}
                  value={form.recoveryCreditPolicy}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      recoveryCreditPolicy: e.target.value as RecoveryCreditPolicy,
                    })
                  }
                >
                  {RECOVERY_CREDIT_POLICIES.map((policy) => (
                    <option key={policy} value={policy}>
                      {humanise(policy)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field
                label="Recovery credit %"
                hint="Fraction of the input cost a recovery is worth (0-100)"
                tooltip="Used by the “percent of input cost” valuation above. 100 means recovered material is worth what it originally cost, so recovering it puts the full value back on the balance sheet; 50 credits half that."
              >
                <input
                  name="recoveryCreditPercent"
                  className={inputClass}
                  value={form.recoveryCreditPercent}
                  onChange={(e) => setForm({ ...form, recoveryCreditPercent: e.target.value })}
                  inputMode="decimal"
                  placeholder="100"
                />
              </Field>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-ink-body">
                  <input
                    name="trackLots"
                    type="checkbox"
                    checked={form.trackLots}
                    onChange={(e) => setForm({ ...form, trackLots: e.target.checked })}
                  />
                  Track lots
                </label>
                <Tooltip
                  subject="Track lots"
                  text="Whether stock of this item is followed batch by batch. Items with lot tracking show every lot separately, with its own expiry and cost — which is what lets a finished product be traced back to the material behind it. Items without it are only counted in total."
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-3">
                <ErrorNote>
                  {errorMessage(saveMutation.error, 'Could not save the item.') ?? ''}
                </ErrorNote>
                <div className="mt-4 flex gap-2">
                  <Button type="submit" disabled={saveMutation.isPending}>
                    {saveMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create item'}
                  </Button>
                  <Button tone="ghost" onClick={closeForm}>
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardBody>
          <FilterBar activeCount={search ? 1 : 0}>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                name="itemSearch"
                className={`${inputClass} max-w-xs`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search code or name"
                aria-label="Search items"
              />
              <label className="flex items-center gap-2 text-sm text-ink-body">
                <input
                  name="includeInactive"
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                />
                Include inactive
              </label>
              {listQuery.isSuccess ? (
                <span className="text-sm text-ink-muted">
                  {listQuery.data.total} item{listQuery.data.total === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
          </FilterBar>

          {listQuery.isPending ? <Spinner /> : null}

          <ErrorNote>
            {errorMessage(listQuery.error, 'Could not load items. Is the API running?') ?? ''}
          </ErrorNote>

          {listQuery.isSuccess ? (
            <DataTable
              rows={items}
              columns={itemColumns}
              rowKey={(item) => item.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actionsHeader="Actions"
              actions={itemActions}
              empty={
                search
                  ? `No items match “${search}”.`
                  : 'No items yet. Use “New item” to add the first one.'
              }
            />
          ) : null}

          <div className="mt-4">
            <ErrorNote>
              {errorMessage(toggleMutation.error, 'Could not change the item status.') ?? ''}
            </ErrorNote>
          </div>
        </CardBody>
      </Card>
    </>
  );
}
